import type {
  CmsCompanyInfo,
  CmsDashboardStats,
  CmsFounder,
  CmsHomepage,
  CmsPortfolioProject,
  CmsSocialLink,
  CmsVisitorAnalytics,
  CmsWorkspaceOverview,
} from "../types";

type LoadState = "idle" | "loading" | "success" | "error";

interface ResourceState<T> {
  data: T | null;
  status: LoadState;
  error: string | null;
  fetchedAt: number | null;
}

function emptyResource<T>(): ResourceState<T> {
  return { data: null, status: "idle", error: null, fetchedAt: null };
}

export interface CmsStoreState {
  homepage: ResourceState<CmsHomepage>;
  portfolio: ResourceState<CmsPortfolioProject[]>;
  founder: ResourceState<CmsFounder>;
  company: ResourceState<CmsCompanyInfo>;
  social: ResourceState<CmsSocialLink[]>;
  analytics: ResourceState<CmsVisitorAnalytics>;
  dashboard: ResourceState<{ stats: CmsDashboardStats; activity: import("../types").CmsActivityItem[] }>;
  workspace: ResourceState<CmsWorkspaceOverview>;
  setResourceLoading: (key: keyof Omit<CmsStoreState, "setResourceLoading" | "setResourceSuccess" | "setResourceError" | "invalidate" | "invalidateAll">) => void;
  setResourceSuccess: <K extends keyof Omit<CmsStoreState, "setResourceLoading" | "setResourceSuccess" | "setResourceError" | "invalidate" | "invalidateAll">>(
    key: K,
    data: NonNullable<CmsStoreState[K]> extends ResourceState<infer T> ? T : never
  ) => void;
  setResourceError: (key: keyof Omit<CmsStoreState, "setResourceLoading" | "setResourceSuccess" | "setResourceError" | "invalidate" | "invalidateAll">, error: string) => void;
  invalidate: (key: keyof Omit<CmsStoreState, "setResourceLoading" | "setResourceSuccess" | "setResourceError" | "invalidate" | "invalidateAll">) => void;
  invalidateAll: () => void;
}

import { create } from "zustand";

const CACHE_MS = 0;

export const useCmsStore = create<CmsStoreState>((set) => ({
  homepage: emptyResource(),
  portfolio: emptyResource(),
  founder: emptyResource(),
  company: emptyResource(),
  social: emptyResource(),
  analytics: emptyResource(),
  dashboard: emptyResource(),
  workspace: emptyResource(),

  setResourceLoading: (key) =>
    set((state) => ({
      [key]: { ...state[key], status: "loading", error: null },
    })),

  setResourceSuccess: (key, data) =>
    set({
      [key]: { data, status: "success", error: null, fetchedAt: Date.now() },
    }),

  setResourceError: (key, error) =>
    set((state) => ({
      [key]: { ...state[key], status: "error", error },
    })),

  invalidate: (key) =>
    set({
      [key]: emptyResource(),
    }),

  invalidateAll: () =>
    set({
      homepage: emptyResource(),
      portfolio: emptyResource(),
      founder: emptyResource(),
      company: emptyResource(),
      social: emptyResource(),
      analytics: emptyResource(),
      dashboard: emptyResource(),
      workspace: emptyResource(),
    }),
}));

export function isCmsCacheFresh(fetchedAt: number | null): boolean {
  if (!fetchedAt) return false;
  return Date.now() - fetchedAt < CACHE_MS;
}
